import type { WorkflowBlockDefinition, WorkflowBlockFieldOption } from "./types"
import { WORKFLOW_OPERATOR_OPTIONS, WORKFLOW_VARIABLE_OPTIONS } from "../variables/registry"

const triggerBlocks: WorkflowBlockDefinition[] = [
  {
    type: "lead.created",
    display_name: "Lead Created",
    category: "trigger",
    icon: "Plus",
    description: "Start when LeadFlow receives a new inbound lead.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [],
    default_config: {},
    runtime_handler: "triggers.lead_created",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "lead.updated",
    display_name: "Lead Updated",
    category: "trigger",
    icon: "RefreshCcw",
    description: "Start when a lead is updated.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [],
    default_config: {},
    runtime_handler: "triggers.lead_updated",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "pipeline.stage_changed",
    display_name: "Stage Changed",
    category: "trigger",
    icon: "GitBranch",
    description: "Start when the pipeline stage changes.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [],
    default_config: {},
    runtime_handler: "triggers.pipeline_stage_changed",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "appointment.booked",
    display_name: "Appointment Booked",
    category: "trigger",
    icon: "CalendarCheck2",
    description: "Start when an appointment is booked.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [],
    default_config: {},
    runtime_handler: "triggers.appointment_booked",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "appointment.cancelled",
    display_name: "Appointment Cancelled",
    category: "trigger",
    icon: "CalendarX2",
    description: "Start when an appointment is cancelled.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [],
    default_config: {},
    runtime_handler: "triggers.appointment_cancelled",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "form.submitted",
    display_name: "Form Submitted",
    category: "trigger",
    icon: "SquareMousePointer",
    description: "Start when a form submission arrives.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [field("form_id", "Form ID", { required: true })],
    default_config: { form_id: "" },
    runtime_handler: "triggers.form_submitted",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "webhook.incoming",
    display_name: "Webhook Incoming",
    category: "trigger",
    icon: "Webhook",
    description: "Start from an internal LeadFlow webhook.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [field("source", "Source")],
    default_config: {},
    runtime_handler: "triggers.webhook_incoming",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "schedule.trigger",
    display_name: "Schedule Trigger",
    category: "trigger",
    icon: "Clock3",
    description: "Start from an internal schedule trigger.",
    inputs: [],
    outputs: [port("output", "Continue")],
    config_schema: [field("schedule_interval_minutes", "Run every", { required: true, type: "duration", description: "Choose an interval. LeadFlow stores the value in minutes." })],
    default_config: { schedule_interval_minutes: 1440 },
    runtime_handler: "triggers.schedule_trigger",
    runtime_available: false,
    draft_only: true,
  },
]

const logicBlocks: WorkflowBlockDefinition[] = [
  {
    type: "condition",
    display_name: "Condition",
    category: "logic",
    icon: "CircleDashed",
    description: "Check whether a lead matches a rule.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Pass"), port("else", "Fail")],
    config_schema: [
      field("field", "Field", { required: true, options: WORKFLOW_VARIABLE_OPTIONS }),
      field("operator", "Operator", { required: true, options: WORKFLOW_OPERATOR_OPTIONS }),
      field("value", "Value", { required: false, description: "The value type changes based on the selected variable and operator." }),
    ],
    default_config: { field: "", operator: "equals", value: "" },
    runtime_handler: "logic.condition",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "delay",
    display_name: "Delay",
    category: "logic",
    icon: "Hourglass",
    description: "Pause before continuing.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("duration_value", "Duration", { required: true, type: "number", description: "Relative wait amount." }),
      field("duration_unit", "Unit", { required: true, type: "select", options: [
        { value: "minutes", label: "Minutes" },
        { value: "hours", label: "Hours" },
        { value: "days", label: "Days" },
      ] }),
    ],
    default_config: { duration_value: 30, duration_unit: "minutes" },
    runtime_handler: "logic.delay",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "wait_until",
    display_name: "Wait Until",
    category: "logic",
    icon: "AlarmClock",
    description: "Pause until a specific date or time.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("target_type", "Target", { required: true, type: "select", options: [
        { value: "specific_datetime", label: "Specific date and time" },
        { value: "next_day_time", label: "Next day at time" },
        { value: "next_weekday_time", label: "Next weekday at time" },
        { value: "datetime_variable", label: "Datetime variable" },
      ] }),
      field("date", "Target date", { required: false, type: "date", visible_when: { field: "target_type", equals: "specific_datetime" } }),
      field("time_of_day", "Target time", { required: false, type: "time", visible_when: { field: "target_type", in: ["specific_datetime", "next_day_time", "next_weekday_time"] } }),
      field("weekday", "Weekday", { required: false, type: "select", visible_when: { field: "target_type", equals: "next_weekday_time" }, options: [
        { value: "monday", label: "Monday" },
        { value: "tuesday", label: "Tuesday" },
        { value: "wednesday", label: "Wednesday" },
        { value: "thursday", label: "Thursday" },
        { value: "friday", label: "Friday" },
        { value: "saturday", label: "Saturday" },
        { value: "sunday", label: "Sunday" },
      ] }),
      field("variable", "Datetime variable", { required: false, type: "select", visible_when: { field: "target_type", equals: "datetime_variable" } }),
      field("timezone", "Timezone", { required: false, type: "select", visible_when: { field: "target_type", not_equals: "datetime_variable" }, options: [{ value: "America/Chicago", label: "America/Chicago" }] }),
    ],
    default_config: { target_type: "specific_datetime", date: "", time_of_day: "", timezone: "America/Chicago" },
    runtime_handler: "logic.wait_until",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "split_path",
    display_name: "Split Path",
    category: "logic",
    icon: "SplitSquareVertical",
    description: "Send the workflow down multiple paths.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("path_a", "Path A"), port("path_b", "Path B")],
    config_schema: [field("paths", "Paths", { required: false, type: "json", description: "Optional display labels for fixed Path 1 and Path 2 outputs." })],
    default_config: { paths: ["Path 1", "Path 2"] },
    runtime_handler: "logic.split_path",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "stop_workflow",
    display_name: "Stop Workflow",
    category: "logic",
    icon: "CircleStop",
    description: "Stop execution after this node.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [],
    config_schema: [field("reason", "Reason")],
    default_config: {},
    runtime_handler: "logic.stop_workflow",
    runtime_available: true,
    draft_only: false,
  },
]


const integrationBlocks: WorkflowBlockDefinition[] = []

const legacyBlocks: WorkflowBlockDefinition[] = [
  {
    type: "fieldroutes.audience",
    display_name: "Legacy FieldRoutes Audience",
    category: "integration",
    icon: "DatabaseZap",
    description: "Legacy unsupported block. FieldRoutes campaigns are now scheduled and executed in Google Apps Script; remove this block from the workflow.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Unsupported")],
    config_schema: [],
    default_config: {},
    runtime_handler: null,
    runtime_available: false,
    draft_only: true,
  },
]

const actionBlocks: WorkflowBlockDefinition[] = [
  {
    type: "email.send",
    display_name: "Send Email",
    category: "action",
    icon: "Mail",
    description: "Send a templated email through the Blue Beetle Apps Script email router.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("template_key", "Template", { type: "select", required: true, options: [
        fieldOption("welcome", "Welcome"),
        fieldOption("mosquito", "Mosquito"),
        fieldOption("termite", "Termite"),
      ], description: "Template key from the cached Apps Script template catalog." }),
      field("recipient_source", "Recipient", { type: "select", required: true, options: [fieldOption("current_contact", "Current LeadFlow contact")] }),
      field("variable_mappings", "Variable mappings", { type: "json", description: "Required variables are resolved safely. name defaults to customer.name." }),
    ],
    default_config: { template_key: "welcome", recipient_source: "current_contact", variable_mappings: { name: "customer.name" } },
    runtime_handler: "actions.send_email",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "sms.send",
    display_name: "Send SMS",
    category: "action",
    icon: "MessageSquareText",
    description: "Reserved editor block for later SMS delivery.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [field("to", "To", { required: true }), field("body", "Body", { required: true })],
    default_config: { to: "", body: "" },
    runtime_handler: null,
    runtime_available: false,
    draft_only: true,
  },
  {
    type: "user.assign",
    display_name: "Assign User",
    category: "action",
    icon: "UserRound",
    description: "Assign the lead to a LeadFlow user.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [field("user_id", "User", { required: true })],
    default_config: { user_id: "" },
    runtime_handler: "actions.user_assign",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "lead.update",
    display_name: "Update Lead",
    category: "action",
    icon: "PencilLine",
    description: "Update a LeadFlow-owned field on the lead.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("field", "Field", { required: true, options: [
        fieldOption("lead.stage_key", "Lead stage", "stage_select"),
        fieldOption("lead.assignee_id", "Assigned user", "user_select"),
        fieldOption("lead.source", "Source"),
        fieldOption("lead.status", "Status"),
        fieldOption("customer.name", "Customer name"),
        fieldOption("customer.phone", "Phone"),
        fieldOption("customer.email", "Email"),
        fieldOption("lead.potential_value", "Potential value", "number"),
        fieldOption("lead.notes", "Notes", "textarea"),
        fieldOption("lead.follow_up_status", "Follow-up status"),
        fieldOption("lead.attention_needed", "Attention needed", "boolean"),
      ]}),
      field("value", "Value", { required: false, description: "The value type changes based on the selected field." }),
    ],
    default_config: { field: "", value: "" },
    runtime_handler: "actions.lead_update",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "tag.add",
    display_name: "Add Tag",
    category: "action",
    icon: "Tags",
    description: "Add a tag to the lead.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [field("tag", "Tag", { required: true })],
    default_config: { tag: "" },
    runtime_handler: "actions.tag_add",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "tag.remove",
    display_name: "Remove Tag",
    category: "action",
    icon: "Tag",
    description: "Remove a tag from the lead.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [field("tag", "Tag", { required: true })],
    default_config: { tag: "" },
    runtime_handler: "actions.tag_remove",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "task.create",
    display_name: "Create Task",
    category: "action",
    icon: "ListTodo",
    description: "Create an internal task or reminder.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("title", "Title", { required: true }),
      field("due_in_minutes", "Due", { type: "duration", description: "Use 0 minutes for immediately." }),
      field("body", "Body"),
    ],
    default_config: { title: "", due_in_minutes: 60, body: "" },
    runtime_handler: "actions.task_create",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "pipeline.move_stage",
    display_name: "Move Pipeline Stage",
    category: "action",
    icon: "KanbanSquare",
    description: "Move the lead to another pipeline stage.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [field("to_stage_key", "Target stage", { required: true, type: "stage_select" })],
    default_config: { to_stage_key: "" },
    runtime_handler: "actions.pipeline_move_stage",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "create_internal_note",
    display_name: "Create Internal Note",
    category: "action",
    icon: "PencilLine",
    description: "Create a note on the lead.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("body", "Body", { required: true }),
      field("category", "Category", { options: [
        fieldOption("general", "General note"),
        fieldOption("call_attempt", "Call attempt"),
        fieldOption("voicemail", "Voicemail"),
        fieldOption("internal_alert", "Internal alert"),
        fieldOption("estimate", "Estimate note"),
        fieldOption("follow_up", "Follow-up note"),
      ]}),
      field("pinned", "Pinned", { type: "boolean" }),
    ],
    default_config: { body: "", category: "internal_alert", pinned: false },
    runtime_handler: "actions.create_internal_note",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "create_follow_up",
    display_name: "Create Follow-up",
    category: "action",
    icon: "Bell",
    description: "Create a follow-up reminder.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [
      field("state", "State", { options: [
        fieldOption("pending", "Pending"),
        fieldOption("claimed", "Claimed"),
        fieldOption("contacted", "Contacted"),
        fieldOption("completed", "Completed"),
        fieldOption("paused", "Paused"),
      ]}),
      field("due_in_minutes", "Due", { type: "duration", description: "Use 0 minutes for immediately." }),
      field("reason", "Reason"),
    ],
    default_config: { state: "pending", due_in_minutes: 60, reason: "" },
    runtime_handler: "actions.create_follow_up",
    runtime_available: true,
    draft_only: false,
  },
  {
    type: "webhook.call",
    display_name: "Call Webhook",
    category: "action",
    icon: "Webhook",
    description: "Reserved editor block for later outbound webhooks.",
    inputs: [port("input", "Input", { required: true })],
    outputs: [port("output", "Continue")],
    config_schema: [field("url", "URL", { required: true }), field("method", "HTTP method", { options: [fieldOption("POST", "POST"), fieldOption("GET", "GET"), fieldOption("PUT", "PUT"), fieldOption("PATCH", "PATCH"), fieldOption("DELETE", "DELETE")] })],
    default_config: { url: "", method: "POST" },
    runtime_handler: null,
    runtime_available: false,
    draft_only: true,
  },
]

export const WORKFLOW_BLOCKS = [...triggerBlocks, ...logicBlocks, ...integrationBlocks, ...actionBlocks]
export const LEGACY_WORKFLOW_BLOCKS = legacyBlocks
export { triggerBlocks, logicBlocks, integrationBlocks, actionBlocks }

export function blockByType(type: string) {
  return WORKFLOW_BLOCKS.find((block) => block.type === type) ?? LEGACY_WORKFLOW_BLOCKS.find((block) => block.type === type)
}

function port(key: string, label: string, options: Partial<WorkflowBlockDefinition["inputs"][number]> = {}) {
  return {
    key,
    label,
    required: options.required ?? false,
    multiple: options.multiple ?? false,
  }
}

function field(key: string, label: string, options: { required?: boolean; type?: WorkflowBlockDefinition["config_schema"][number]["type"]; options?: WorkflowBlockDefinition["config_schema"][number]["options"]; description?: string; visible_when?: WorkflowBlockDefinition["config_schema"][number]["visible_when"] } = {}) {
  return {
    key,
    label,
    type: options.type ?? "string",
    required: options.required ?? false,
    description: options.description ?? null,
    options: options.options ?? null,
    visible_when: options.visible_when ?? null,
  }
}

function fieldOption(value: string, label: string, kind?: WorkflowBlockFieldOption["kind"]) {
  return { value, label, kind }
}
